import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { PortfolioHistory } from '../types';

export type HyperliquidPortfolioHistoryValue = {
  /** Portfolio history per wallet (keyed by walletId) */
  portfolioHistoryByWallet: Map<string, PortfolioHistory>;
};

export const HyperliquidPortfolioHistoryContext =
  createContext<HyperliquidPortfolioHistoryValue | null>(null);

type HyperliquidPortfolioHistoryProviderProps = {
  children: ReactNode;
  portfolioHistoryByWallet: Map<string, PortfolioHistory>;
};

/**
 * Lightweight provider for surfaces that need balance calculations without
 * mounting the full HyperliquidProvider and its market data side effects.
 */
export function HyperliquidPortfolioHistoryProvider({
  children,
  portfolioHistoryByWallet,
}: HyperliquidPortfolioHistoryProviderProps) {
  const value = useMemo(
    () => ({ portfolioHistoryByWallet }),
    [portfolioHistoryByWallet]
  );

  return (
    <HyperliquidPortfolioHistoryContext.Provider value={value}>
      {children}
    </HyperliquidPortfolioHistoryContext.Provider>
  );
}

export function useHyperliquidPortfolioHistory() {
  const context = useContext(HyperliquidPortfolioHistoryContext);
  if (!context) {
    throw new Error(
      'useHyperliquidPortfolioHistory must be used within HyperliquidPortfolioHistoryContext'
    );
  }
  return context;
}
